<template>
  <div class="general-ledger">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter acc-select">
          <label>Account</label>
          <FormDropdown v-model="filters.account" :options="accountOptions" placeholder="Cash in hand" searchable clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Voucher, particulars, party or narration" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-flag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Opening Balance</div>
          <div class="rp-card-value">{{ balanceText(summary.opening) }}</div>
          <div class="rp-card-sub">on {{ formatDate(filters.date_from) }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-arrow-down-left"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Debits</div>
          <div class="rp-card-value">{{ formatMoney(summary.debit) }}</div>
          <div class="rp-card-sub">{{ plural(summary.voucher_count, 'voucher') }} in the period</div>
        </div>
      </div>
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-arrow-up-right"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Credits</div>
          <div class="rp-card-value">{{ formatMoney(summary.credit) }}</div>
          <div class="rp-card-sub">{{ plural(summary.line_count, 'posting') }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-scale"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Closing Balance</div>
          <div class="rp-card-value">{{ balanceText(summary.closing) }}</div>
          <div class="rp-card-sub">on {{ formatDate(filters.date_to) }}</div>
        </div>
      </div>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Account</div>
        <div class="fw-semibold">{{ summary.account.name }}</div>
        <small class="text-muted d-block mb-2">Code {{ summary.account.id || '—' }} · {{ summary.account.type }}</small>
        <ul class="rp-highlights">
          <li class="tone-navy">
            <span class="hl-icon"><i class="ti ti-binary-tree"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">Covers</small>
              <div class="fw-semibold">
                {{ summary.account.group ? `Group of ${plural(summary.account.accounts, 'account')}` : 'One ledger account' }}
              </div>
            </div>
          </li>
          <li class="tone-teal">
            <span class="hl-icon"><i class="ti ti-arrows-left-right"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">Normal balance</small>
              <div class="fw-semibold">{{ summary.natural_side === 'Dr' ? 'Debit' : 'Credit' }}</div>
            </div>
          </li>
        </ul>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Movement by voucher type</div>
        <div v-if="typeBars.length" class="rp-bars">
          <div v-for="entry in typeBars" :key="entry.key">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ entry.label }} <span class="text-muted">· {{ entry.count }}</span></span>
              <span class="text-nowrap">
                <span v-if="Number(entry.debit)" class="text-success">Dr {{ formatMoney(entry.debit) }}</span>
                <span v-if="Number(entry.debit) && Number(entry.credit)" class="text-muted"> · </span>
                <span v-if="Number(entry.credit)" class="text-danger">Cr {{ formatMoney(entry.credit) }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill info" :style="{ width: `${entry.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No postings in the period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Balance movement</div>
        <div class="rp-bars">
          <div v-for="step in flowBars" :key="step.label">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ step.label }}</span>
              <span class="fw-semibold text-nowrap">{{ step.text }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="step.tone" :style="{ width: `${step.percent}%` }"></div></div>
          </div>
        </div>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold">
          {{ summary.account.name }}
          <span class="text-muted fw-normal small">· {{ plural(rows.length, 'posting') }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher</th>
              <th v-if="summary.account.group">Account</th>
              <th>Particulars</th>
              <th>Party</th>
              <th class="text-end">Debit</th>
              <th class="text-end">Credit</th>
              <th class="text-end">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="page === 1 && !loading" class="acc-opening">
              <td>{{ formatDate(filters.date_from) }}</td>
              <td colspan="2" v-if="summary.account.group"></td>
              <td v-else></td>
              <td>Opening balance</td>
              <td></td>
              <td></td>
              <td></td>
              <td class="text-end acc-balance">{{ balanceParts(summary.opening).amount }}<span class="acc-side">{{ balanceParts(summary.opening).side }}</span></td>
            </tr>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="text-nowrap">{{ formatDate(row.date) }}</td>
              <td>
                <div class="fw-medium text-nowrap">{{ row.voucher_id }}</div>
                <span class="rp-badge" :class="typeTone(row.type_key)">{{ row.type }}</span>
              </td>
              <td v-if="summary.account.group">
                <button type="button" class="acc-link" title="Open this account's ledger" @click="filters.account = row.account_id">
                  {{ row.account }}
                </button>
              </td>
              <td>
                <div>{{ row.particulars }}</div>
                <span v-if="row.narration" class="rp-sub text-truncate" :title="row.narration">{{ row.narration }}</span>
              </td>
              <td>{{ row.party || '—' }}</td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.debit) }">{{ amountText(row.debit) }}</td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.credit) }">{{ amountText(row.credit) }}</td>
              <td class="text-end fw-semibold acc-balance">{{ balanceParts(row.balance).amount }}<span class="acc-side">{{ balanceParts(row.balance).side }}</span></td>
            </tr>
            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="summary.account.group ? 8 : 7">
                <div class="rp-empty">
                  <i class="ti ti-book"></i>
                  <div class="fw-semibold">No postings found</div>
                  <small class="text-muted">Try another period, account or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="!loading">
            <tr>
              <th :colspan="summary.account.group ? 5 : 4" class="text-end">Total · closing balance</th>
              <th class="text-end">{{ formatMoney(summary.debit) }}</th>
              <th class="text-end">{{ formatMoney(summary.credit) }}</th>
              <th class="text-end acc-balance">{{ balanceParts(summary.closing).amount }}<span class="acc-side">{{ balanceParts(summary.closing).side }}</span></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="rows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="rows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Approved postings of {{ options.store || 'the store' }}. A group account takes in all the accounts under it. Particulars name the
      account on the other side of the voucher; the balance runs Dr − Cr.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport from './accountingReportMixin';

const EMPTY_SUMMARY = {
  account: { id: null, name: '—', type: '', group: false, accounts: 0 }, natural_side: 'Dr',
  opening: 0, debit: 0, credit: 0, closing: 0, line_count: 0, voucher_count: 0, by_type: [],
};

export default {
  name: 'GeneralLedgerReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.general_ledger_report.get,
  fileName: 'general_ledger',
  columns: [],

  data() {
    return {
      filters: { ...defaultPeriod(), account: this.$route?.query?.account || null, search: '' },
      summary: { ...EMPTY_SUMMARY },
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || !!(this.filters.account || this.filters.search.trim());
    },

    visibleRows() {
      return this.rows;
    },

    typeBars() {
      return this.withPercent(
        this.summary.by_type.map((t) => ({ ...t, total: Number(t.debit) + Number(t.credit) })),
        'total',
      );
    },

    flowBars() {
      const s = this.summary;
      const steps = [
        { label: 'Opening balance', value: s.opening, text: this.balanceText(s.opening), tone: 'info' },
        { label: 'Debits', value: s.debit, text: `+${this.formatMoney(s.debit)}`, tone: 'success' },
        { label: 'Credits', value: s.credit, text: `−${this.formatMoney(s.credit)}`, tone: 'danger' },
        { label: 'Closing balance', value: s.closing, text: this.balanceText(s.closing), tone: 'warning' },
      ];
      const max = Math.max(0, ...steps.map((st) => Math.abs(Number(st.value) || 0)));
      return steps.map((st) => {
        const v = Math.abs(Number(st.value) || 0);
        return { ...st, percent: max && v ? Math.max(4, Math.round((v / max) * 100)) : 0 };
      });
    },
  },

  watch: {
    'filters.account'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...(this.filters.account ? { account: this.filters.account } : {}) };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), account: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    balanceParts(value) {
      const n = Number(value) || 0;
      return { amount: this.formatMoney(Math.abs(n)), side: this.drcr(n) };
    },

    printReport() {
      const s = this.summary;
      const search = this.filters.search.trim();
      this.printStatement(
        `General Ledger — ${s.account.name} (${s.account.id})`,
        [this.periodText(), this.storeLabel(), search && `Search: ${search}`],
        [
          ['Opening', this.balanceText(s.opening)],
          ['Debits', this.formatMoney(s.debit)],
          ['Credits', this.formatMoney(s.credit)],
          ['Closing', this.balanceText(s.closing)],
        ],
        [
          { label: 'Date', value: (r) => (r.date ? this.formatDate(r.date) : '') },
          { label: 'Voucher', value: (r) => r.voucher_id || '' },
          { label: 'Type', value: (r) => r.type || '' },
          ...(s.account.group ? [{ label: 'Account', value: (r) => r.account || '' }] : []),
          { label: 'Particulars', value: (r) => r.particulars },
          { label: 'Party', value: (r) => r.party || '' },
          { label: 'Narration', value: (r) => r.narration || '' },
          { label: 'Debit', value: (r) => (r.debit === undefined ? '' : this.amountText(r.debit)), num: true },
          { label: 'Credit', value: (r) => (r.credit === undefined ? '' : this.amountText(r.credit)), num: true },
          { label: 'Balance', value: (r) => this.balanceText(r.balance), num: true },
        ],
        [{ particulars: 'Opening balance', balance: s.opening, _bold: true }, ...this.rows],
        { Particulars: 'Closing balance', Debit: this.formatMoney(s.debit), Credit: this.formatMoney(s.credit), Balance: this.balanceText(s.closing) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
